import { useEffect, useRef, useState } from "react";
import { Terminal, Loader2, CornerDownLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CommandPanel = ({ send, subscribe, live, os }) => {
  const isMac = (os || "").toLowerCase().includes("mac") || (os || "").toLowerCase().includes("darwin");
  const [cmd, setCmd] = useState("");
  const [shell, setShell] = useState("auto");
  const [output, setOutput] = useState("");
  const [running, setRunning] = useState(false);
  const reqRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => subscribe((m) => {
    if (m.type !== "cmd_output" || m.req !== reqRef.current) return;
    if (m.data) setOutput((o) => o + m.data);
    if (m.last) {
      setRunning(false);
      setOutput((o) => `${o}\n[Terminé${m.code !== undefined ? `, code ${m.code}` : ""}]\n`);
    }
  }), [subscribe]);

  useEffect(() => { if (!live) setRunning(false); }, [live]);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [output]);

  const run = (e) => {
    e?.preventDefault();
    if (!cmd.trim() || running || !live) return;
    const req = `cmd_${Date.now()}`;
    reqRef.current = req;
    setOutput((o) => `${o}$ ${cmd}\n`);
    setRunning(true);
    send({ type: "cmd", req, command: cmd, shell });
    setCmd("");
  };

  return (
    <aside className="w-[460px] flex-shrink-0 border-l border-slate-800 bg-[#0B1120] flex flex-col" data-testid="command-panel">
      <div className="px-4 h-12 flex items-center gap-2 border-b border-slate-800">
        <Terminal className="h-4 w-4 text-sky-400" />
        <p className="font-mono text-[11px] tracking-widest text-slate-400 flex-1">EXÉCUTER UNE COMMANDE</p>
        {isMac ? (
          <span className="font-mono text-[11px] text-slate-500" data-testid="cmd-shell-label">bash</span>
        ) : (
          <Select value={shell} onValueChange={setShell}>
            <SelectTrigger className="h-7 w-32 text-xs" data-testid="cmd-shell-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto</SelectItem>
              <SelectItem value="powershell">PowerShell</SelectItem>
              <SelectItem value="cmd">Invite (cmd)</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      <pre data-testid="cmd-output" className="flex-1 overflow-y-auto p-4 font-mono text-[12px] text-emerald-300 whitespace-pre-wrap break-words">
        {output || "La sortie des commandes s'affiche ici.\nPowerShell sur Windows, bash sur macOS."}
        <span ref={endRef} />
      </pre>
      <form onSubmit={run} className="p-3 border-t border-slate-800 flex gap-2">
        <input
          data-testid="cmd-input"
          value={cmd}
          onChange={(e) => setCmd(e.target.value)}
          disabled={!live}
          placeholder={live ? "ex. ipconfig /all, Get-Service…" : "Machine hors ligne"}
          className="flex-1 bg-slate-900 border border-slate-700 rounded px-3 py-2 font-mono text-sm text-slate-100 outline-none focus:border-sky-500 disabled:opacity-50"
        />
        <Button type="submit" disabled={!cmd.trim() || running || !live} data-testid="cmd-run-btn">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <CornerDownLeft className="h-4 w-4" />}
        </Button>
      </form>
    </aside>
  );
};
